'use client';

import { useEffect, useRef } from 'react';
import { Dialog as DialogPrimitive, Heading, Text } from 'react-aria-components';
import { FaXmark } from 'react-icons/fa6';

import type { HeadingProps } from 'react-aria-components';
import type { ButtonProps } from './Button';

import { cn, composeTailwindRenderProps } from '@/utils';
import { Button } from './Button';

const Dialog = ({
	role = 'dialog',
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive>) => {
	return (
		<DialogPrimitive
			role={role}
			className={cn(
				'peer/dialog group/dialog relative flex max-h-[inherit] flex-col overflow-hidden outline-hidden [scrollbar-width:thin] [&::-webkit-scrollbar]:size-0.5',
				className,
			)}
			{...props}
		/>
	);
};

const DialogTrigger = (props: ButtonProps) => <Button {...props} />;

type DialogHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
	title?: string;
	description?: string;
};

const DialogHeader = ({ className, ...props }: DialogHeaderProps) => {
	const headerRef = useRef<HTMLHeadingElement>(null);

	useEffect(() => {
		const header = headerRef.current;
		if (!header) {
			return;
		}

		const observer = new ResizeObserver(entries => {
			for (const entry of entries) {
				header.parentElement?.style.setProperty(
					'--dialog-header-height',
					`${entry.target.clientHeight}px`,
				);
			}
		});

		observer.observe(header);
		return () => observer.unobserve(header);
	}, []);

	return (
		<div
			data-slot="dialog-header"
			ref={headerRef}
			className={cn(
				'relative flex flex-col gap-1 p-6 [&[data-slot=dialog-header]:has(+[data-slot=dialog-footer])]:pb-0',
				className,
			)}
		>
			{props.title && <DialogTitle>{props.title}</DialogTitle>}
			{props.description && <DialogDescription>{props.description}</DialogDescription>}
			{!props.title && typeof props.children === 'string' ? (
				<DialogTitle {...props} />
			) : (
				props.children
			)}
		</div>
	);
};

interface DialogTitleProps extends Omit<HeadingProps, 'level'> {
	level?: 1 | 2 | 3 | 4;
	ref?: React.Ref<HTMLHeadingElement>;
}
const DialogTitle = ({ level = 2, className, ref, ...props }: DialogTitleProps) => (
	<Heading
		slot="title"
		level={level}
		ref={ref}
		className={cn(
			'text-light-heading flex flex-1 items-center font-semibold',
			level === 1 && 'text-xl',
			level === 2 && 'text-lg',
			level === 3 && 'text-md',
			level === 4 && 'text-sm',
			className,
		)}
		{...props}
	/>
);

type DialogDescriptionProps = React.ComponentProps<'div'>;
const DialogDescription = ({ className, ref, ...props }: DialogDescriptionProps) => (
	<Text
		slot="description"
		className={cn('text-light-text text-sm', className)}
		ref={ref}
		{...props}
	/>
);

type DialogBodyProps = React.ComponentProps<'div'>;
const DialogBody = ({ className, ref, ...props }: DialogBodyProps) => (
	<div
		data-slot="dialog-body"
		ref={ref}
		className={cn(
			'isolate flex max-h-[calc(var(--visual-viewport-height)-var(--visual-viewport-vertical-padding)-var(--dialog-header-height,0px)-var(--dialog-footer-height,0px))] flex-1 flex-col overflow-auto px-6',
			className,
		)}
		{...props}
	/>
);

type DialogFooterProps = React.ComponentProps<'div'>;
const DialogFooter = ({ className, ...props }: DialogFooterProps) => {
	const footerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const footer = footerRef.current;

		if (!footer) {
			return;
		}

		const observer = new ResizeObserver(entries => {
			for (const entry of entries) {
				footer.parentElement?.style.setProperty(
					'--dialog-footer-height',
					`${entry.target.clientHeight}px`,
				);
			}
		});

		observer.observe(footer);
		return () => {
			observer.unobserve(footer);
		};
	}, []);
	return (
		<div
			ref={footerRef}
			data-slot="dialog-footer"
			className={cn(
				'isolate mt-auto flex flex-col-reverse justify-end gap-4 p-6 pb-8 md:flex-row',
				className,
			)}
			{...props}
		/>
	);
};

const DialogClose = ({ className, variant = 'secondary', ...props }: ButtonProps) => {
	return <Button slot="close" className={className} variant={variant} {...props} />;
};

interface CloseButtonIndicatorProps extends Omit<ButtonProps, 'children'> {
	className?: string;
	isDismissable?: boolean | undefined;
}

const DialogCloseIcon = ({ className, ...props }: CloseButtonIndicatorProps) => {
	return props.isDismissable ? (
		<Button
			variant="icon"
			aria-label="Close"
			slot="close"
			className={composeTailwindRenderProps(className, 'close absolute top-2 right-2 z-50 grid')}
		>
			<FaXmark className="size-icon-sm" />
		</Button>
	) : null;
};

export type {
	DialogHeaderProps,
	DialogTitleProps,
	DialogBodyProps,
	DialogFooterProps,
	DialogDescriptionProps,
	CloseButtonIndicatorProps,
};
export {
	Dialog,
	DialogClose,
	DialogTrigger,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogBody,
	DialogFooter,
	DialogCloseIcon,
};
